// src/utils/opsTime.js
//
// Pass 49B §7 — canonical operational timezone. SAS scheduling wall-clock is Asia/Tokyo and
// must NOT be interpreted in the viewer's browser timezone. Asia/Tokyo is a FIXED UTC+9 with
// no DST, so this is exact and dependency-free: display and date-bucketing go through Intl
// pinned to the zone; wall-clock → instant construction uses the fixed +09:00 offset.

export const OPS_TZ = 'Asia/Tokyo';
export const OPS_OFFSET = '+09:00';
const DAY_MS = 24 * 60 * 60 * 1000;

// Pass 82C.2 §7 — the GENERIC neutral timezone fallback. Generic zoned helpers NEVER silently fall back to
// Asia/Tokyo (that is a SAS value); a caller that wants SAS/JST semantics passes 'Asia/Tokyo' explicitly (or uses the
// legacy ops* helpers). LEGACY_OPS_TIMEZONE names the SAS compatibility boundary for code that deliberately keeps JST.
export const NEUTRAL_TZ = 'UTC';
export const LEGACY_OPS_TIMEZONE = OPS_TZ;

function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const _keyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: OPS_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
const _timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: OPS_TZ, hour: '2-digit', minute: '2-digit', hour12: false });

// YYYY-MM-DD calendar date in Asia/Tokyo (the canonical day bucket for Day/Week/Season).
export function opsDateKey(v) {
  const d = toDate(v);
  if (!d) return '';
  return _keyFmt.format(d); // en-CA → "YYYY-MM-DD"
}

// HH:MM wall-clock in Asia/Tokyo, independent of the browser timezone.
export function opsTimeLabel(v) {
  const d = toDate(v);
  if (!d) return '';
  return _timeFmt.format(d);
}

// {year, month(1-12), day} of the instant, in Asia/Tokyo.
export function opsDateParts(v) {
  const key = opsDateKey(v);
  if (!key) return null;
  const [year, month, day] = key.split('-').map(Number);
  return { year, month, day };
}

// The UTC instant for a wall-clock time in Asia/Tokyo. dateStr = 'YYYY-MM-DD', timeStr = 'HH:MM'.
// e.g. opsInstant('2026-12-19','09:00') → the instant of 2026-12-19 09:00 JST, regardless of
// the machine's local timezone.
export function opsInstant(dateStr, timeStr = '00:00') {
  if (!dateStr) return null;
  const t = /^\d{2}:\d{2}/.test(timeStr) ? timeStr.slice(0, 5) : '00:00';
  const d = new Date(`${dateStr}T${t}:00${OPS_OFFSET}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

// ── Pass 82C.1 — GENERIC, organization-timezone-safe wall-clock ⇄ instant (DST-correct, browser-TZ-independent). ──
// The offset (ms) of an IANA timezone AT a given instant, computed via Intl (no external deps), h23 to avoid the
// midnight-'24' quirk. offset = (wall-clock read in the zone, interpreted as if UTC) − the real instant.
function zoneOffsetMs(timeZone, date) {
  const dtf = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const p = {};
  for (const part of dtf.formatToParts(date)) if (part.type !== 'literal') p[part.type] = part.value;
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return asUTC - date.getTime();
}

// The exact UTC instant for a wall-clock (YYYY-MM-DD + HH:MM) in an arbitrary IANA timezone. Two-pass to be correct
// across DST transitions. NEVER depends on the browser/runtime timezone. Pass 82C.2 §7 — defaults to the NEUTRAL zone
// (UTC), never Asia/Tokyo: generic callers MUST pass the ORGANIZATION's timezone; the SAS/JST drop-in is opsInstant().
export function wallClockToInstant(dateStr, timeStr = '00:00', timeZone = NEUTRAL_TZ) {
  if (!dateStr) return null;
  const t = /^\d{2}:\d{2}/.test(timeStr) ? timeStr.slice(0, 5) : '00:00';
  const [y, mo, d] = String(dateStr).split('-').map(Number);
  const [hh, mm] = t.split(':').map(Number);
  if ([y, mo, d, hh, mm].some((n) => Number.isNaN(n))) return null;
  const asUTC = Date.UTC(y, mo - 1, d, hh, mm, 0);
  const off1 = zoneOffsetMs(timeZone, new Date(asUTC));
  let instant = asUTC - off1;
  const off2 = zoneOffsetMs(timeZone, new Date(instant)); // DST-boundary refinement
  if (off2 !== off1) instant = asUTC - off2;
  const out = new Date(instant);
  return Number.isNaN(out.getTime()) ? null : out;
}

// YYYY-MM-DD calendar date of an instant, in an arbitrary IANA timezone (generic version of opsDateKey).
export function zonedDateKey(v, timeZone = NEUTRAL_TZ) {
  const d = toDate(v);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

// HH:MM wall-clock of an instant, in an arbitrary IANA timezone (generic version of opsTimeLabel).
export function zonedTimeLabel(v, timeZone = NEUTRAL_TZ) {
  const d = toDate(v);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-GB', { timeZone, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }).format(d);
}

// ── Pass 82C.2 §6 — the rest of the GENERIC zoned family the LIVE operations workspace needs. Each REQUIRES an
// explicit IANA zone (neutral-UTC fallback), mirrors an ops* helper exactly when given 'Asia/Tokyo', and never
// depends on the browser/runtime timezone. These replace the JST-hardcoded ops* helpers in tenant-generic code. ──

// {year, month(1-12), day} of an instant, in an arbitrary IANA timezone (generic version of opsDateParts).
export function zonedDateParts(v, timeZone = NEUTRAL_TZ) {
  const key = zonedDateKey(v, timeZone);
  if (!key) return null;
  const [year, month, day] = key.split('-').map(Number);
  return { year, month, day };
}

// Human date label (e.g. "Sat 19 Dec 2026") in an arbitrary IANA timezone (generic version of opsDateLabel).
export function zonedDateLabel(v, timeZone = NEUTRAL_TZ) {
  const d = toDate(v);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).format(d);
}

// The 00:00 instant of the zoned day containing v (generic version of opsStartOfDay).
export function zonedStartOfDay(v, timeZone = NEUTRAL_TZ) {
  const key = zonedDateKey(v, timeZone);
  return key ? wallClockToInstant(key, '00:00', timeZone) : null;
}

// Shift by whole zoned CALENDAR days, returning the 00:00 instant of the resulting zoned day. Calendar arithmetic is
// done in UTC (DST-safe: a shifted day is never 23/25h short/long because we re-anchor the target day's midnight in
// the zone). Generic version of opsShiftDays.
export function zonedShiftDays(v, n, timeZone = NEUTRAL_TZ) {
  const parts = zonedDateParts(v, timeZone);
  if (!parts) return null;
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day) + n * DAY_MS);
  const key = `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}`;
  return wallClockToInstant(key, '00:00', timeZone);
}

// Monday-00:00 that starts the zoned week containing v (generic version of opsWeekStart).
export function zonedWeekStart(v, timeZone = NEUTRAL_TZ) {
  const start = zonedStartOfDay(v, timeZone);
  if (!start) return null;
  const wd = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(start);
  const order = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  return zonedShiftDays(start, -(order[wd] ?? 0), timeZone);
}

// Minutes since midnight (0..1439) in an arbitrary IANA timezone — DST-correct via the instant's actual zone offset.
// Generic version of opsMinutesOfDay; used for AM/PM/Night slot classification independent of the viewer's timezone.
export function zonedMinutesOfDay(v, timeZone = NEUTRAL_TZ) {
  const d = toDate(v);
  if (!d) return null;
  const localMs = d.getTime() + zoneOffsetMs(timeZone, d);
  const mins = Math.floor(localMs / 60000) % (24 * 60);
  return ((mins % (24 * 60)) + 24 * 60) % (24 * 60);
}

// Start-of-day instant (00:00 JST) for the JST day containing v.
export function opsStartOfDay(v) {
  const key = opsDateKey(v);
  return key ? opsInstant(key, '00:00') : null;
}

// Shift a date/key by whole JST days, returning the 00:00-JST instant of the result day.
export function opsShiftDays(v, n) {
  const start = opsStartOfDay(v);
  if (!start) return null;
  return opsStartOfDay(new Date(start.getTime() + n * DAY_MS));
}

// Monday-00:00-JST that starts the JST week containing v.
export function opsWeekStart(v) {
  const start = opsStartOfDay(v);
  if (!start) return null;
  // Weekday in JST
  const wd = new Intl.DateTimeFormat('en-US', { timeZone: OPS_TZ, weekday: 'short' }).format(start);
  const order = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  return opsShiftDays(start, -(order[wd] ?? 0));
}

// Minutes since Asia/Tokyo midnight (0..1439). Exact via the fixed +9h offset — used for
// AM/PM/Night classification and any period logic, independent of the viewer's timezone.
export function opsMinutesOfDay(v) {
  const d = toDate(v);
  if (!d) return null;
  const jstMs = d.getTime() + 9 * 60 * 60 * 1000;
  const mins = Math.floor(jstMs / 60000) % (24 * 60);
  return ((mins % (24 * 60)) + 24 * 60) % (24 * 60);
}

// "now" — used only where a genuine current operational instant is wanted (current season).
export function opsNow() { return new Date(); }

// Pass 82C.2 §15 — resolve the operative timezone for a booking-like record. Order (spec §15): the record's OWN
// stored timezone WINS (a materialized case child / manual booking / non-JST tenant booking carries its zone) → an
// explicit option (an org-aware caller supplies the selected org's zone) → the LEGACY SAS/JST fallback LAST (imported
// SAS rows predate per-org timezones; SAS timed bookings already store 'Asia/Tokyo', so this only guards edge rows).
export function resolveBookingTimeZone(rec = {}, { timeZone = '' } = {}) {
  const bt = rec && (rec.timezone || rec.timeZone);
  if (bt) return bt;
  if (timeZone) return timeZone;
  return LEGACY_OPS_TIMEZONE;
}

// Pass 82C.2 §18 — a short, human timezone abbreviation for date/week headers (never raw IANA jargon). SAS's
// Asia/Tokyo shows the familiar 'JST'; other zones use the Intl short name (e.g. 'MST'/'MDT' for America/Denver).
export function zonedTzAbbrev(timeZone = NEUTRAL_TZ, v = new Date()) {
  if (timeZone === OPS_TZ) return 'JST';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(v);
    const tzn = parts.find((p) => p.type === 'timeZoneName');
    return (tzn && tzn.value) || timeZone;
  } catch (e) { return timeZone; }
}

// Human date label (e.g. "Sat 19 Dec 2026") in Asia/Tokyo.
export function opsDateLabel(v) {
  const d = toDate(v);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-GB', { timeZone: OPS_TZ, weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).format(d);
}
